import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Pagination } from '~/components/common/pagination';
import { Button } from '~/components/ui/button';
import { Skeleton } from '~/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '~/components/ui/table';
import type { ProductLeaf } from '~/features/deliveries/types/delivery-runs.types';

import { useFunnelUpstreamProducts } from '../../hooks/use-batch-funnel';
import type {
  FunnelCurationRejectedResponse,
  FunnelUpstreamRef,
} from '../../types/batch-funnel.api.types';
import { withoutErrorCode } from '../../utils/batch-funnel';

/** Sum of every count in a `{kind → {code → n}}` map. */
function sumNested(map: Record<string, Record<string, number>> | null | undefined): number {
  return Object.values(map ?? {}).reduce(
    (sum, byCode) => sum + Object.values(byCode).reduce((s, n) => s + n, 0),
    0,
  );
}

interface FunnelUpstreamProductsProps {
  jobId: string;
  upstreamRef: FunnelUpstreamRef;
  /** `REJECTED` drill-ins keep only destination-rejected rows for this reason (§5.2). */
  rejectedReason: string | null;
  /** Curated product rows open the per-product trace. */
  onOpenTrace: (productId: string) => void;
}

/**
 * Renders the upstream listing behind a drill-in (`source: curation | integrations`)
 * by following its gateway-absolute `productsPath`: curated products, a sample of
 * rejected feed rows ("showing N of M examples"), or per-product delivery results.
 */
export function FunnelUpstreamProducts({
  jobId,
  upstreamRef,
  rejectedReason,
  onOpenTrace,
}: FunnelUpstreamProductsProps) {
  const { t } = useTranslation('catalog-operations');
  const [page, setPage] = useState(0);
  // The funnel can label a delivery failure with its own reason code (e.g.
  // `DELIVERY_FAILED`) while integrations recorded a different per-product code
  // (e.g. `UNATTRIBUTED_PUSH_FAILURE`), so the `errorCode`-filtered list comes
  // back empty. In that case switch straight to the same outcome filter without
  // the code — no extra click — and say so in a note above the list.
  const broadened = withoutErrorCode(upstreamRef.productsPath);
  const [showAllFailed, setShowAllFailed] = useState(false);
  const effectiveRef =
    showAllFailed && broadened ? { ...upstreamRef, productsPath: broadened.path } : upstreamRef;
  const { data, isLoading, isError, isFetching } = useFunnelUpstreamProducts(
    jobId,
    effectiveRef,
    page,
  );
  const codeFilterMatchedNothing =
    !showAllFailed &&
    broadened !== null &&
    !rejectedReason &&
    page === 0 &&
    !isFetching &&
    data?.kind === 'delivery-products' &&
    data.data.products.length === 0;
  // Adjusting state during render (React's documented pattern) rather than in
  // an effect: the widened query starts on this same pass, so the empty
  // filtered result never paints — the skeleton shows until the list lands.
  if (codeFilterMatchedNothing) setShowAllFailed(true);

  if (isLoading || codeFilterMatchedNothing) return <ListSkeleton />;
  if (isError || !data) {
    return (
      <p className="text-error text-sm" data-testid="batch-funnel-drill-upstream-error">
        {t('batchFunnel.drill.listError')}
      </p>
    );
  }

  if (data.kind === 'curation-products') {
    const rows = data.data.data;
    return (
      <div className="space-y-3">
        {rows.length === 0 ? (
          <EmptyList />
        ) : (
          <Table data-testid="batch-funnel-drill-curation-table">
            <TableHeader>
              <TableRow>
                <TableHead>{t('batchFunnel.drill.colProduct')}</TableHead>
                <TableHead>{t('batchFunnel.drill.colBrand')}</TableHead>
                <TableHead>{t('batchFunnel.drill.colState')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => (
                <TableRow
                  key={row.feed_product_id}
                  data-testid={`batch-funnel-drill-curation-row-${row.feed_product_id}`}
                  className="cursor-pointer"
                  onClick={() => {
                    onOpenTrace(row.feed_product_id);
                  }}
                >
                  <TableCell>
                    <p className="text-foreground font-medium">{row.title ?? '—'}</p>
                    <p className="text-muted-foreground font-mono text-xs break-all">
                      {row.product?.sku ?? row.source_product_id ?? row.feed_product_id}
                    </p>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {row.product?.brand ?? '—'}
                  </TableCell>
                  <TableCell className="text-muted-foreground">{row.state ?? '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        <Pagination
          currentPage={page + 1}
          totalPages={data.data.pagination.totalPages}
          testId="batch-funnel-drill-curation-pagination"
          onPageChange={(next) => {
            setPage(next - 1);
          }}
        />
      </div>
    );
  }

  if (data.kind === 'curation-rejected') {
    return <RejectedRows response={data.data} page={page} onPageChange={setPage} />;
  }

  // Delivery results: a REJECTED drill keeps only rows the destination rejected
  // for this reason; FAILED / SUCCEEDED paths already carry their filter.
  const rows = rejectedReason
    ? data.data.products.filter(
        (p) => p.syncStatus === 'REJECTED' && p.syncStatusReason === rejectedReason,
      )
    : data.data.products;
  return (
    <div className="space-y-3">
      {showAllFailed && broadened && (
        <p
          className="bg-warning-50 text-foreground rounded-lg px-3 py-2 text-xs"
          data-testid="batch-funnel-drill-broadened-banner"
        >
          {t('batchFunnel.drill.broadenedNote', { code: broadened.errorCode })}
        </p>
      )}
      {rows.length === 0 ? <EmptyList /> : <DeliveryRows rows={rows} />}
      <div
        className="flex items-center justify-end gap-2"
        data-testid="batch-funnel-drill-delivery-pagination"
      >
        <Button
          data-testid="batch-funnel-drill-delivery-prev-button"
          variant="outline"
          size="sm"
          className="cursor-pointer gap-1 disabled:cursor-not-allowed"
          disabled={page === 0 || isFetching}
          onClick={() => {
            setPage((p) => Math.max(0, p - 1));
          }}
        >
          <ChevronLeft className="h-4 w-4" aria-hidden="true" />
          {t('batchFunnel.drill.prev')}
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="cursor-pointer gap-1 disabled:cursor-not-allowed"
          disabled={!data.data.hasMore || isFetching}
          data-testid="batch-funnel-drill-delivery-next-button"
          onClick={() => {
            setPage((p) => p + 1);
          }}
        >
          {t('batchFunnel.drill.next')}
          <ChevronRight className="h-4 w-4" aria-hidden="true" />
        </Button>
      </div>
    </div>
  );
}

/** Rejected / excluded feed rows — a capped sample, so the header states how
 *  many of the full count are listable. */
function RejectedRows({
  response,
  page,
  onPageChange,
}: {
  response: FunnelCurationRejectedResponse;
  page: number;
  onPageChange: (page: number) => void;
}) {
  const { t } = useTranslation('catalog-operations');
  const total = sumNested(response.counts?.by_reason);
  const sampled = sumNested(response.counts?.sampled);
  return (
    <div className="space-y-3">
      {total > 0 && (
        <p className="text-muted-foreground text-xs" data-testid="batch-funnel-drill-sample-note">
          {t('batchFunnel.drill.sampleNote', {
            shown: sampled.toLocaleString(),
            total: total.toLocaleString(),
          })}
        </p>
      )}
      {response.data.length === 0 && page === 0 && sampled === 0 ? (
        // Curation kept no example rows for this reason (older runs predate the
        // sample, or the reason isn't sampled) — the funnel's count is still
        // right, there's just nothing to list. Say so instead of "No products".
        <p
          className="text-muted-foreground py-6 text-center text-sm"
          data-testid="batch-funnel-drill-no-sample-empty"
        >
          {t('batchFunnel.drill.noSampleRecorded')}
        </p>
      ) : response.data.length === 0 ? (
        <EmptyList />
      ) : (
        <Table data-testid="batch-funnel-drill-rejected-table">
          <TableHeader>
            <TableRow>
              <TableHead>{t('batchFunnel.drill.colSourceId')}</TableHead>
              <TableHead>{t('batchFunnel.drill.colReason')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {response.data.map((row, index) => {
              const title = typeof row.detail?.title === 'string' ? row.detail.title : null;
              return (
                <TableRow
                  key={`${row.source_product_id ?? ''}:${String(index)}`}
                  data-testid={`batch-funnel-drill-rejected-row-${row.source_product_id ?? String(index)}`}
                >
                  <TableCell>
                    {title && <p className="text-foreground font-medium">{title}</p>}
                    <p className="text-muted-foreground font-mono text-xs break-all">
                      {row.source_product_id ?? '—'}
                    </p>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {[row.reason_kind, row.reason_code].filter(Boolean).join(' · ')}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
      <Pagination
        currentPage={page + 1}
        totalPages={response.pagination.totalPages}
        testId="batch-funnel-drill-rejected-pagination"
        onPageChange={(next) => {
          onPageChange(next - 1);
        }}
      />
    </div>
  );
}

/** Per-product delivery outcome rows (integration-service listing). */
function DeliveryRows({ rows }: { rows: ProductLeaf[] }) {
  const { t } = useTranslation('catalog-operations');
  return (
    <Table data-testid="batch-funnel-drill-delivery-table">
      <TableHeader>
        <TableRow>
          <TableHead>{t('batchFunnel.drill.colProduct')}</TableHead>
          <TableHead>{t('batchFunnel.drill.colOutcome')}</TableHead>
          <TableHead>{t('batchFunnel.drill.colDestination')}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row, index) => (
          <TableRow
            key={`${row.sku}:${String(index)}`}
            data-testid={`batch-funnel-drill-delivery-row-${row.sku}`}
          >
            <TableCell>
              <p className="text-foreground font-medium">{row.title ?? row.sku}</p>
              <p className="text-muted-foreground font-mono text-xs break-all">
                {row.externalId ?? row.sku}
              </p>
            </TableCell>
            <TableCell>
              <p className="text-foreground">{row.reasonLabel ?? row.outcome}</p>
              {row.errorMessage && (
                <p className="text-muted-foreground text-xs break-words">{row.errorMessage}</p>
              )}
            </TableCell>
            <TableCell>
              <p className="text-foreground">{row.syncStatus ?? '—'}</p>
              {(row.syncStatusMessage ?? row.syncStatusReason) && (
                <p className="text-muted-foreground text-xs break-words">
                  {row.syncStatusMessage ?? row.syncStatusReason}
                </p>
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

/** Loading rows shared by every drill-in table. */
export function ListSkeleton() {
  return (
    <div className="space-y-2" data-testid="batch-funnel-drill-loading">
      {Array.from({ length: 6 }, (_, i) => (
        <Skeleton key={i} className="h-10 w-full" />
      ))}
    </div>
  );
}

/** Empty-page message shared by every drill-in table. */
export function EmptyList() {
  const { t } = useTranslation('catalog-operations');
  return (
    <p
      className="text-muted-foreground py-6 text-center text-sm"
      data-testid="batch-funnel-drill-empty"
    >
      {t('batchFunnel.drill.empty')}
    </p>
  );
}
